import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { tickets } from '@repletoryreach/sdk-api';
import { Button, Card, Group, SimpleGrid, Skeleton, Stack, Text, Title } from '@repletoryreach/sdk-ui';
import { Alert, DataTable, EmptyState, MetricCard, PageHeader, StatusBadge } from '@repletoryreach/sdk-ui/data-display';
import { BarChart } from '@repletoryreach/sdk-ui/charts';
import { SegmentedControl, Select, TextInput } from '@repletoryreach/sdk-ui/inputs';
import { HelpButton } from '../../../app/guides/HelpButton';
import { useT } from '../../../i18n/I18nProvider';
import { formatCount } from '../../../lib/format';
import type { Indexed } from '../../../lib/types';
import { addDays, formatLocalDate, todayIn } from '../../bookings/lib/time';
import { useDateLocale, useOrgTimezone } from '../../bookings/lib/data';
import { TICKETS_KEY, categoryLabel, categoryTone, formatMinutes, ticketErrorText, useLookups } from '../lib/data';
import '../tickets.css';

type Preset = '7' | '30' | '90' | 'custom';

const percent = (v: number | null | undefined, none: string) => (v == null ? none : `${Math.round(v)}%`);
const score = (v: number | null | undefined, none: string) => (v == null ? none : formatCount(Math.round(v * 10) / 10));

export default function TicketReportsPage() {
  const t = useT();
  const tz = useOrgTimezone();
  const locale = useDateLocale();
  const lk = useLookups();
  const today = todayIn(tz);
  const [preset, setPreset] = useState<Preset>('30');
  const [from, setFrom] = useState(() => addDays(today, -29));
  const [to, setTo] = useState(today);
  const [teamId, setTeamId] = useState('');

  function choose(p: Preset) {
    setPreset(p);
    if (p !== 'custom') { setFrom(addDays(today, -(Number(p) - 1))); setTo(today); }
  }

  const valid = !!from && !!to && from <= to;
  const q = useQuery({
    queryKey: [TICKETS_KEY, 'report', from, to, teamId],
    queryFn: () => tickets.getReport({ from, to, team_id: teamId || undefined }),
    enabled: valid
  });
  const r = q.data?.data;
  const none = t('common.none');

  return (
    <Stack gap="lg">
      <PageHeader title={t('tickets.reports.title')} description={t('tickets.reports.description')} withDivider={false}
        actions={<HelpButton guide="tickets" />} />

      <Group gap="sm" wrap="wrap" align="flex-end">
        <SegmentedControl size="sm" value={preset} onChange={(v) => choose(v as Preset)}
          data={[
            { value: '7', label: t('tickets.reports.last', { n: 7 }) },
            { value: '30', label: t('tickets.reports.last', { n: 30 }) },
            { value: '90', label: t('tickets.reports.last', { n: 90 }) },
            { value: 'custom', label: t('tickets.reports.custom') }
          ]} />
        <TextInput size="sm" type="date" label={t('tickets.reports.from')} value={from} max={to || undefined}
          onChange={(e) => { setFrom(e.currentTarget.value); setPreset('custom'); }} />
        <TextInput size="sm" type="date" label={t('tickets.reports.to')} value={to} min={from || undefined}
          onChange={(e) => { setTo(e.currentTarget.value); setPreset('custom'); }}
          error={!valid ? t('tickets.reports.badRange') : undefined} />
        {lk.teams.length > 0 && (
          <Select size="sm" label={t('tickets.field.team')} value={teamId} onChange={(e) => setTeamId(e.currentTarget.value)}
            data={[{ value: '', label: t('tickets.filter.anyTeam') }, ...lk.teams.map((x) => ({ value: x.id, label: x.name }))]} />
        )}
      </Group>

      {q.error && (
        <Alert color="error" role="alert">
          <Group gap="sm" justify="space-between" wrap="wrap">
            <span>{ticketErrorText(t, q.error)}</span>
            <Button size="xs" variant="default" onClick={() => void q.refetch()}>{t('action.retry')}</Button>
          </Group>
        </Alert>
      )}

      <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md" data-guide="tickets-reports">
        <MetricCard label={t('tickets.reports.created')} value={r ? formatCount(r.created) : ''} loading={q.isPending && valid} />
        <MetricCard label={t('tickets.reports.solved')} value={r ? formatCount(r.solved) : ''} loading={q.isPending && valid} />
        <MetricCard label={t('tickets.reports.firstResponse')} value={r ? formatMinutes(t, r.first_response_minutes_avg) : ''} hint={t('tickets.reports.average')} loading={q.isPending && valid} />
        <MetricCard label={t('tickets.reports.resolution')} value={r ? formatMinutes(t, r.resolution_minutes_avg) : ''} hint={t('tickets.reports.average')} loading={q.isPending && valid} />
        <MetricCard label={t('tickets.reports.slaMet')} value={r ? percent(r.sla_met_percent, none) : ''} hint={t('tickets.reports.slaMetHint')} loading={q.isPending && valid} />
        <MetricCard label={t('tickets.reports.csat')} value={r ? score(r.csat_avg, none) : ''}
          hint={r ? t('tickets.reports.csatHint', { count: r.csat_count }) : undefined} loading={q.isPending && valid} />
      </SimpleGrid>

      {r && (
        <Card withBorder>
          <Stack gap="sm">
            <Title order={4}>{t('tickets.reports.openNow')}</Title>
            <Group gap="sm" wrap="wrap">
              {(['new', 'open', 'pending', 'on_hold', 'solved'] as const).map((c) => (
                <StatusBadge key={c} status={categoryTone(c)} size="sm">
                  {categoryLabel(t, c)}: {formatCount(r.open_now_by_category?.[c] ?? 0)}
                </StatusBadge>
              ))}
            </Group>
          </Stack>
        </Card>
      )}

      <Card withBorder>
        <Stack gap="sm">
          <Title order={4}>{t('tickets.reports.byDay')}</Title>
          {q.isPending && valid ? <Skeleton height={240} /> : r && r.by_day.length > 0 ? (
            <BarChart
              data={r.by_day as unknown as Record<string, unknown>[]}
              dataKey="date"
              h={260}
              series={[
                { name: 'created', label: t('tickets.reports.created'), color: 'var(--rr-color-primary)' },
                { name: 'solved', label: t('tickets.reports.solved'), color: 'var(--rr-color-success)' }
              ]}
              valueFormatter={(v) => formatCount(v)}
              labelFormatter={(v) => formatLocalDate(String(v), locale, { day: 'numeric', month: 'short' })}
            />
          ) : (
            <EmptyState title={t('tickets.reports.noDays')} description={t('tickets.reports.noDaysHint')} />
          )}
        </Stack>
      </Card>

      <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="lg">
        <Card withBorder>
          <Stack gap="sm">
            <Title order={4}>{t('tickets.reports.byTeam')}</Title>
            {r && r.by_team.length === 0 ? <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('tickets.reports.noRows')}</Text> : (
              <DataTable<Indexed<tickets.ReportTeamRow>>
                records={(r?.by_team ?? []) as Indexed<tickets.ReportTeamRow>[]}
                loading={q.isPending && valid}
                idAccessor={(x, i) => x.team_id ?? `none-${i}`}
                columns={[
                  { accessor: 'team_id', title: t('tickets.field.team'), render: (x) => <Text role="bodySmall">{x.team_id ? lk.teamName(x.team_id) || t('common.unknown') : t('tickets.noTeam')}</Text> },
                  { accessor: 'created', title: t('tickets.reports.created'), textAlign: 'right', render: (x) => formatCount(x.created) },
                  { accessor: 'solved', title: t('tickets.reports.solved'), textAlign: 'right', render: (x) => formatCount(x.solved) },
                  { accessor: 'sla_met_percent', title: t('tickets.reports.slaMet'), textAlign: 'right', render: (x) => percent(x.sla_met_percent, none) }
                ]}
              />
            )}
          </Stack>
        </Card>
        <Card withBorder>
          <Stack gap="sm">
            <Title order={4}>{t('tickets.reports.byPerson')}</Title>
            {r && r.by_assignee.length === 0 ? <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('tickets.reports.noRows')}</Text> : (
              <DataTable<Indexed<tickets.ReportAssigneeRow>>
                records={(r?.by_assignee ?? []) as Indexed<tickets.ReportAssigneeRow>[]}
                loading={q.isPending && valid}
                idAccessor={(x, i) => x.user_id ?? `none-${i}`}
                columns={[
                  { accessor: 'user_id', title: t('tickets.field.assignee'), render: (x) => <Text role="bodySmall">{x.user_id ? lk.personLabel(x.user_id) || t('common.unknown') : t('tickets.unassigned')}</Text> },
                  { accessor: 'solved', title: t('tickets.reports.solved'), textAlign: 'right', render: (x) => formatCount(x.solved) },
                  { accessor: 'first_response_minutes_avg', title: t('tickets.reports.firstResponse'), textAlign: 'right', render: (x) => formatMinutes(t, x.first_response_minutes_avg) },
                  { accessor: 'csat_avg', title: t('tickets.reports.csat'), textAlign: 'right', render: (x) => score(x.csat_avg, none) }
                ]}
              />
            )}
          </Stack>
        </Card>
      </SimpleGrid>
    </Stack>
  );
}
